/**
 * ShortsPromoBanner — Bandeau promo "Vos Shorts arrivent sur Code 2 Roues".
 *
 * Version courte prévue pour la Home et le top de /shorts. Mise en page :
 *   - Fond dégradé rose/rouge discret, bordure lumineuse
 *   - Chip "NOUVEAU" pulsant
 *   - Titre fort + une ligne benefit-driven
 *   - 2 CTA : Proposer un Short / Voir les Shorts
 *   - Mention légale discrète en bas (modération + droits)
 *
 * Props :
 *   - variant : 'home' (full width, grand) | 'shorts' (compact top-of-feed) | 'minimal'
 *   - dismissible : si true, le visiteur peut la fermer (persist localStorage)
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clapperboard, Sparkles, X, ArrowRight } from 'lucide-react';

const STORAGE_KEY = 'c2r-shorts-banner-dismissed';

export default function ShortsPromoBanner({ variant = 'home', dismissible = true }) {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!dismissible) return;
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === '1') setHidden(true);
    } catch { /* ignore */ }
  }, [dismissible]);

  if (hidden) return null;

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    setHidden(true);
  };

  // Compact variant pour le top de /shorts
  if (variant === 'minimal') {
    return (
      <div
        className="relative rounded-xl border border-rose-500/30 bg-gradient-to-r from-rose-950/70 via-red-900/40 to-black/60 p-3 flex items-center gap-3 overflow-hidden"
        data-testid="shorts-promo-minimal"
      >
        <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-rose-500/20 text-rose-300">
          <Clapperboard className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-[0.14em] text-rose-300 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Nouveau
          </div>
          <p className="text-[13px] text-white font-semibold leading-snug">
            Vos vidéos. Vos astuces. Notre communauté.
          </p>
          <p className="text-[11px] text-white/60 leading-snug mt-0.5">
            Proposez un Short — sélection éditoriale sur Code 2 Roues (moto) & CodeVoiture.fr (auto).
          </p>
        </div>
        <Link
          to="/shorts/proposer"
          className="shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded-full bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-black uppercase tracking-wider transition"
          data-testid="shorts-promo-cta-minimal"
        >
          Proposer
          <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  // Home — version visuelle avec vrais BÉNÉFICES
  return (
    <section
      className="relative rounded-2xl border border-rose-500/30 bg-gradient-to-br from-black via-rose-950/50 to-black overflow-hidden"
      data-testid="shorts-promo-home"
      aria-labelledby="shorts-promo-title"
    >
      {/* Grain overlay pour texture (opacité très faible) */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: '3px 3px',
        }}
        aria-hidden="true"
      />

      {dismissible && (
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/5 hover:bg-white/15 text-white/60 hover:text-white grid place-items-center transition z-10"
          aria-label="Fermer l'annonce"
          data-testid="shorts-promo-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}

      <div className="relative p-6 sm:p-8 lg:p-10 grid lg:grid-cols-[1fr,auto] gap-6 items-center">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-rose-300 mb-3">
            <Sparkles className="w-3 h-3" />
            <span>Lancement</span>
            <span className="relative flex items-center justify-center w-2 h-2 ml-1">
              <span className="absolute inset-0 rounded-full bg-rose-500 animate-ping opacity-75" />
              <span className="relative rounded-full w-2 h-2 bg-rose-500" />
            </span>
          </div>

          <h2
            id="shorts-promo-title"
            className="text-white text-[22px] sm:text-[28px] lg:text-[32px] font-black leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            🚀 Vos Shorts arrivent sur Code&nbsp;2&nbsp;Roues
          </h2>
          <p className="text-white/75 text-[14px] sm:text-[15px] leading-relaxed mt-2 max-w-2xl">
            Vous apprenez à conduire ? Vous avez une <strong className="text-white">astuce, une expérience ou une vidéo</strong> qui pourrait aider d'autres élèves ? Partagez-la.
          </p>
          <ul className="mt-3 space-y-1 text-[13px] text-white/70 leading-relaxed">
            <li className="flex items-start gap-2">
              <span className="text-rose-400 shrink-0">🎬</span>
              Une manœuvre, un geste technique, un conseil pour un freinage, un plateau moto, un évitement…
            </li>
            <li className="flex items-start gap-2">
              <span className="text-rose-400 shrink-0">💡</span>
              Un Short TikTok, YouTube, Vimeo — avec quelques mots sur ce qu'il vous a appris.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-rose-400 shrink-0">🏍️</span>
              Les contributions validées sont mises en avant sur <strong className="text-white">Code 2 Roues</strong> (moto) et <strong className="text-white">CodeVoiture.fr</strong> (auto).
            </li>
          </ul>

          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <Link
              to="/shorts/proposer"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-rose-600 hover:bg-rose-500 text-white text-[12px] font-black uppercase tracking-wider shadow-lg shadow-rose-900/40 transition"
              data-testid="shorts-promo-cta-propose"
            >
              <Clapperboard className="w-4 h-4" />
              Proposer un Short
            </Link>
            <Link
              to="/shorts"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full border border-white/20 text-white hover:bg-white/5 text-[12px] font-black uppercase tracking-wider transition"
              data-testid="shorts-promo-cta-see"
            >
              Voir les Shorts
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <p className="mt-4 text-[11px] text-white/40 leading-snug max-w-xl">
            Les vidéos proposées ne sont pas publiées automatiquement. Leur diffusion
            dépend de la validation éditoriale, des droits d'utilisation et des conditions
            d'intégration applicables.
          </p>
        </div>

        {/* Visuel décoratif — pastille clap animée */}
        <div className="hidden lg:flex items-center justify-center pr-2 pointer-events-none select-none">
          <div className="relative w-28 h-28">
            <div className="absolute inset-0 rounded-full bg-rose-500/15 animate-pulse" />
            <div className="absolute inset-2 rounded-full bg-gradient-to-br from-rose-600 to-red-800 flex items-center justify-center shadow-2xl shadow-rose-900/40">
              <Clapperboard className="w-10 h-10 text-white" strokeWidth={2.2} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
